import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import TrackerDetailOverviewTab from "./TrackerDetailOverviewTab";
import TrackerDetailInfoTab from "./TrackerDetailInfoTab";
import TrackerDetailPeopleTab from "./TrackerDetailPeopleTab";
import TrackerDetailSubmissionsTab from "./TrackerDetailSubmissionsTab";
import TrackerDetailAutomationsTab from "./TrackerDetailAutomationsTab";
import { getTrackerDetailApi } from "src/DAL/tracker/tracker";
import {
  ensureTrackerShape,
  isLeadCaptureTracker,
  normalizeTracker,
  TRACKER_STATUS,
} from "./trackerStorage";
import {
  countUniquePeople,
  loadSubmissionsFromApi,
} from "./submissionStorage";
import { loadAutomationsFromApi } from "./automations/automationStorage";
import { backTarget } from "./trackerNav";

const PRIMARY = "var(--portal-theme-primary)";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";

const formatTabLabel = (label, count) =>
  count == null ? label : `${label} (${count})`;

const JOURNEY_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "Members", countKey: "people" },
  { id: "submissions", label: "Submissions", countKey: "submissions" },
  { id: "automations", label: "Automations", countKey: "automations" },
];

const LEAD_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "Leads", countKey: "people" },
  { id: "submissions", label: "Submissions", countKey: "submissions" },
  { id: "automations", label: "Automations", countKey: "automations" },
];

export default function TrackerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Arrow: back to the submission / review / list that opened this tracker; "All trackers" stays the list.
  const back = backTarget(location, navigate, "/trackers", "All trackers");
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabCounts, setTabCounts] = useState({
    people: null,
    submissions: null,
    automations: null,
  });

  const tabParam = searchParams.get("tab") || "overview";
  const isLead = isLeadCaptureTracker(tracker);
  const DETAIL_TABS = isLead ? LEAD_DETAIL_TABS : JOURNEY_DETAIL_TABS;
  const activeTab = DETAIL_TABS.some((t) => t.id === tabParam)
    ? tabParam
    : "overview";

  const setActiveTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    if (nextTab === "overview") next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  const fetchTrackerDetail = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    let message = "Tracker not found";
    try {
      const res = await getTrackerDetailApi(id);
      if (res?.code === 200 && res.tracker) {
        setTracker(ensureTrackerShape(normalizeTracker(res.tracker)));
        return;
      }
      if (res?.message) message = res.message;
    } catch {
      message = "Could not load tracker";
    } finally {
      setLoading(false);
    }

    enqueueSnackbar(message, { variant: "error" });
    navigate("/trackers", { replace: true });
  }, [id, enqueueSnackbar, navigate]);

  useEffect(() => {
    fetchTrackerDetail();
  }, [fetchTrackerDetail]);

  const trackerKey = tracker?.id || tracker?._id || "";
  useEffect(() => {
    if (!trackerKey) return undefined;
    let cancelled = false;
    setTabCounts({ people: null, submissions: null, automations: null });
    (async () => {
      const [subs, autos] = await Promise.all([
        loadSubmissionsFromApi(trackerKey, { limit: 500 }),
        loadAutomationsFromApi(trackerKey),
      ]);
      if (cancelled) return;
      const rows = subs?.rows || [];
      setTabCounts({
        people: countUniquePeople(rows),
        submissions: subs?.total ?? rows.length,
        automations: (autos?.automations || []).length,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [trackerKey]);

  const statusLabel = useMemo(() => {
    if (!tracker) return "Draft";
    if (
      tracker.status === TRACKER_STATUS.LIVE ||
      tracker.status === "published"
    ) {
      return "Live";
    }
    if (tracker.status === TRACKER_STATUS.ARCHIVED) return "Archived";
    return "Draft";
  }, [tracker]);

  const statusColor = useMemo(() => {
    if (statusLabel === "Live") return "success";
    if (statusLabel === "Archived") return "default";
    return "warning";
  }, [statusLabel]);

  if (loading && !tracker) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={36} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!tracker) return null;

  const trackerId = tracker.id || tracker._id || id;

  return (
    <div className="container-fluid tracker-module-page">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <IconButton
              size="small"
              onClick={back.go}
              aria-label={back.label}
              sx={{ p: 0.75 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              component="button"
              type="button"
              onClick={() => navigate("/trackers")}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                cursor: "pointer",
                color: MUTED,
                fontSize: 13,
                "&:hover": { color: PRIMARY },
              }}
            >
              All trackers
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
                maxWidth: 360,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tracker.name ||
                (isLead ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Label variant="ghost" color={statusColor}>
              {statusLabel}
            </Label>
          </Box>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 20, md: 22 },
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {tracker.name ||
              (isLead ? "Untitled lead capture" : "Untitled journey")}
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13.5, color: MUTED }}>
            {isLead ? "Lead Capture · Standard Tracker" : "Journey Tracker"}
            {tracker.updatedAt
              ? ` · updated ${moment(tracker.updatedAt).format(
                  "DD MMM YYYY h:mm A"
                )}`
              : ""}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            className="small-contained-button"
            onClick={() =>
              navigate(
                `/tracker-submissions?tracker=${encodeURIComponent(trackerId)}`
              )
            }
          >
            <ListAltOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Submissions
          </button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          mb: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {DETAIL_TABS.map((t) => (
          <Chip
            key={t.id}
            label={formatTabLabel(
              t.label,
              t.countKey ? tabCounts[t.countKey] : null
            )}
            onClick={() => setActiveTab(t.id)}
            sx={{
              fontWeight: 700,
              cursor: "pointer",
              bgcolor: activeTab === t.id ? PRIMARY : "#fff",
              color: activeTab === t.id ? "#fff" : "#0f172a",
              border: `1px solid ${
                activeTab === t.id ? PRIMARY : BORDER
              }`,
              "&:hover": {
                bgcolor: activeTab === t.id ? PRIMARY : "#f8fafc",
              },
            }}
          />
        ))}
      </Box>

      {activeTab === "overview" && (
        <TrackerDetailOverviewTab tracker={tracker} />
      )}
      {activeTab === "details" && <TrackerDetailInfoTab tracker={tracker} />}
      {activeTab === "people" && <TrackerDetailPeopleTab tracker={tracker} />}
      {activeTab === "submissions" && (
        <TrackerDetailSubmissionsTab tracker={tracker} />
      )}
      {activeTab === "automations" && (
        <TrackerDetailAutomationsTab tracker={tracker} />
      )}
    </div>
  );
}
